import { useState } from "react";
import MarkdownComponents from "../../utils/MarkdownComponents";

const markdown = `
# Create Ticket Type

To add a new ticket type, fill out the form and click **Create Ticket Type**.

## Ticket Type Creation Form

\`\`\`
+-------------------------------+
|    New Ticket Type Form       |
+-------------------------------+
| Name:         [___________]   |
| Display Name: [___________]   |
|      [Create Ticket Type]     |
+-------------------------------+
\`\`\`

---

## What Happens After Submit?

1. The user fills the form and clicks **Create Ticket Type**.
2. The frontend sends a POST request to the backend:

    \`\`\`http
    POST https://simplisysapi.vizionsys.com/gateway/TicketType
    Content-Type: application/json
    Authorization: Bearer <token>
    Tenant-Code: vizionsys
    ... (see headers in UI)
    \`\`\`

3. If successful, the backend responds with the new ticket type data (including generated ID).

---

## Security Note

- Only authorized users should create ticket types.
- Always use HTTPS.
`;

const API_URL = "https://simplisysapi.vizionsys.com/gateway/TicketType";
const API_HEADERS = {
  "accept": "*/*",
  "Content-Type": "application/json",
  "Authorization": "Bearer Sekwo58",
  "Tenant-Code": "vizionsys",
  "origin": "https://simplisys.vizionsys.com",
  "priority": "u=1, i",
  "referer": "https://simplisys.vizionsys.com/",
  };

const ANIMATION_STEPS = [
  { label: "Sending ticket type creation request to API...", key: "request" },
  { label: "Received new ticket type data from server!", key: "response" },
  { label: "Ticket type creation complete! 🎉", key: "done" },
];

const defaultForm = {
  name: "typname",
  displayName: "typnamedd",
};

export default function TicketTypeCreationPage() {
  const [form, setForm] = useState(defaultForm);
  const [apiData, setApiData] = useState(null);
  const [animationStep, setAnimationStep] = useState(-1);
  const [copyMsg, setCopyMsg] = useState("");

  // Simulate backend ticket type creation
  const fakeCreateTicketType = (payload) =>
    new Promise((resolve) =>
      setTimeout(
        () =>
          resolve({
            id: 2955,
            parentId: 1,
            name: payload.name,
            displayName: payload.displayName,
            description: null,
            isActive: true,
            json: null,
          }),
        1000
      )
    );

  const animate = (res) => {
    setAnimationStep(0);
    setApiData({
      url: API_URL,
      headers: API_HEADERS,
      payload: form,
      response: res,
    });
    setTimeout(() => {
      setAnimationStep(1);
      setTimeout(() => {
        setAnimationStep(2);
      }, 900);
    }, 900);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAnimationStep(-1);
    setApiData(null);
    const res = await fakeCreateTicketType(form);
    animate(res);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // cURL command generator with all headers
  const curl = apiData
    ? `curl -X POST ${apiData.url} \\
${Object.entries(apiData.headers)
  .map(([k, v]) => `  -H "${k}: ${v}" \\`)
  .join("\n")}
  -d '${JSON.stringify(apiData.payload, null, 2)}'`
    : "";

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopyMsg("Copied!");
    setTimeout(() => setCopyMsg(""), 1200);
  };

  return (
    <>
      <MarkdownComponents markdown={markdown} />

      <form
        onSubmit={handleSubmit}
        style={{
          marginTop: 24,
          background: "#f7f9fa",
          padding: 24,
          borderRadius: 8,
          boxShadow: "0 2px 8px #0001",
        }}
      >
        <h3 style={{ margin: "0 0 16px 0" }}>API Request Preview</h3>
        <div>
          <strong>URL:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {API_URL}
          </pre>
        </div>
        <div>
          <strong>Headers:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {JSON.stringify(API_HEADERS, null, 2)}
          </pre>
        </div>
        <div>
          <strong>Payload:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {JSON.stringify(form, null, 2)}
          </pre>
        </div>
        <div>
          <strong>cURL Command:</strong>
          <pre style={{ background: "#f4f4f4", padding: 8, marginBottom: 6 }}>
            {curl}
          </pre>
          <button
            type="button"
            onClick={() => handleCopy(curl)}
            style={{
              background: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "4px 10px",
              cursor: "pointer",
              fontSize: 13,
              marginBottom: 8,
            }}
          >
            Copy cURL
          </button>
          <span style={{ color: "#007bff", marginLeft: 8 }}>{copyMsg}</span>
        </div>

        {/* Animation Section */}
        {animationStep >= 0 && apiData && (
          <div
            style={{
              marginTop: 32,
              background: "#fff",
              padding: 24,
              borderRadius: 8,
              boxShadow: "0 2px 8px #0001",
            }}
          >
            <h3>Ticket Type Creation Progress</h3>
            <ul style={{ listStyle: "none", padding: 0 }}>
              {ANIMATION_STEPS.map((step, idx) => (
                <li
                  key={step.key}
                  style={{
                    opacity: animationStep >= idx ? 1 : 0.3,
                    fontWeight: animationStep === idx ? "bold" : "normal",
                    transition: "opacity 0.4s",
                    marginBottom: 10,
                  }}
                >
                  {step.label}
                  {step.key === "response" && animationStep >= idx && (
                    <div style={{ fontSize: 13, color: "#444", marginTop: 6 }}>
                      <strong>Response:</strong>
                      <pre style={{ background: "#f4f4f4", padding: 8 }}>
                        {JSON.stringify(apiData.response, null, 2)}
                      </pre>
                    </div>
                  )}
                  {step.key === "done" && animationStep >= idx && (
                    <div style={{ marginTop: 8, color: "#28a745" }}>
                      <strong>🎉 Ticket Type Created Successfully!</strong>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Minimal form fields */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label>
              Name:
              <input
                name="name"
                required
                value={form.name}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <label>
              Display Name:
              <input
                name="displayName"
                required
                value={form.displayName}
                onChange={handleChange}
                style={{ width: "100%", marginTop: 4, padding: 8 }}
              />
            </label>
          </div>
        </div>
        <button
          type="submit"
          style={{
            marginTop: 16,
            width: "100%",
            padding: 10,
            background: "#007bff",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          Create Ticket Type
        </button>
      </form>
    </>
  );
}
